import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Hover intent: how long the mouse has to stay on a menu item before its submenu opens. */
const ACTIVATE_DELAY = 75;
const DEACTIVATE_DELAY = 350;

/**
 * While the mouse heads from an open submenu's item into the submenu, the items it crosses on the
 * way only take over once it rests on them for this long.
 */
const AIM_REST_DELAY = 200;

/** How far back (in ms) mouse positions are kept to work out which way the mouse is heading. */
const AIM_SAMPLE_WINDOW = 100;

/**
 * Touch screens can follow a tap with emulated mouse events. Mouse events this soon after a touch
 * are ignored, so they can't close a submenu the tap just opened.
 */
const TOUCH_MOUSE_GRACE = 800;

/** A click only counts as the end of a press that started on the same item this recently. */
const PRESS_TIMEOUT = 1500;

/**
 * A custom element that manages a header menu.
 *
 * Mouse users open submenus by hovering, keyboard users by focusing a top-level item, and touch
 * users by tapping: the first tap on an item opens its submenu and a second tap follows its link.
 * Escape closes an open submenu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {Object} Press
 * @property {HTMLElement} item - The menu item the press started on.
 * @property {string} pointerType - The pointer type of the press.
 * @property {boolean} wasOpen - Whether the item's submenu was already open when the press started.
 * @property {number} timeStamp - When the press started.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.overflowMenu?.addEventListener(
      'pointerleave',
      (event) => {
        if (this.#isMouse(event)) this.#debouncedDeactivate();
      },
      { signal }
    );
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { capture: true, signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#debouncedActivate.cancel();
    this.#debouncedDeactivate.cancel();
    this.#cancelPending();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * The press that started on a top-level item, waiting for its click.
   * @type {Press | null}
   */
  #press = null;

  /** When the last touch or pen contact happened. */
  #lastTouch = -Infinity;

  /**
   * Recent mouse positions, to work out whether the mouse is heading into the open submenu.
   * @type {{ x: number, y: number, t: number }[]}
   */
  #trail = [];

  /**
   * A menu item the mouse crossed on its way into the open submenu, waiting to see whether the mouse
   * rests on it.
   * @type {Element | null}
   */
  #pendingItem = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pendingTimer;

  /** Set while focus is moved back to a top-level item, so that doesn't reopen its submenu. */
  #restoringFocus = false;

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the menu item the mouse entered or that received focus
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    const listItem = event.target;

    if (!(listItem instanceof Element)) return;

    if (event.type === 'focus') {
      if (!this.#restoringFocus) this.#activateItem(listItem);
      return;
    }

    // Touch and pen open submenus with a tap instead, see #onClick
    if (!this.#isMouse(event)) return;

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivate.cancel();
    this.#cancelPending();
    this.#trackPointer(/** @type {PointerEvent} */ (event));

    const activeItem = this.#state.activeItem;

    // Back on the open item, or inside its submenu
    if (activeItem && findMenuItem(listItem) === activeItem) return;

    if (!activeItem) {
      this.#debouncedActivate(listItem);
    } else if (this.#isHeadingIntoSubmenu()) {
      // The mouse is only crossing this item on its way into the open submenu
      this.#pendingItem = listItem;
      this.#restartPendingTimer();
    } else {
      this.#activateItem(listItem);
    }
  };

  /**
   * Activate a menu item and open its submenu
   * @param {Element} listItem - The menu list item, or the "More" item
   */
  #activateItem(listItem) {
    this.#debouncedActivate.cancel();
    this.#debouncedDeactivate.cancel();
    this.#cancelPending();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    let item = findMenuItem(listItem);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = listItem.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  }

  #debouncedActivate = debounce((/** @type {Element} */ listItem) => this.#activateItem(listItem), ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    const listItem = event.target;

    if (!(listItem instanceof Element)) return;
    if (event.type !== 'blur' && !this.#isMouse(event)) return;

    this.#debouncedActivate.cancel();

    // The mouse moved off an item it crossed on the way to the open submenu, without reaching it
    if (listItem === this.#pendingItem) {
      this.#cancelPending();
      this.#debouncedDeactivate();
      return;
    }

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (findMenuItem(listItem) === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options] - `force` closes the submenu even while the overflow menu is hovered
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered && !force) return;

    this.#cancelPending();

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   */
  #debouncedDeactivate = debounce(() => this.#deactivate(), DEACTIVATE_DELAY);

  /**
   * Whether a pointer event comes from a mouse, rather than touch, a pen or a mouse event emulated after a tap
   * @param {Event} event
   * @returns {boolean}
   */
  #isMouse(event) {
    return (
      /** @type {PointerEvent} */ (event).pointerType === 'mouse' && event.timeStamp - this.#lastTouch > TOUCH_MOUSE_GRACE
    );
  }

  /**
   * Remember the item a press starts on, and whether its submenu was already open: focusing the item
   * on press can open it before the click arrives
   * @param {PointerEvent} event
   */
  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse') this.#lastTouch = event.timeStamp;

    const control = event.target instanceof Element ? event.target.closest('.menu-list__link') : null;
    const listItem = control?.closest('.menu-list__list-item');
    const item = listItem ? findMenuItem(listItem) : null;

    this.#press = item
      ? {
          item,
          pointerType: event.pointerType,
          wasOpen: item === this.#state.activeItem,
          timeStamp: event.timeStamp,
        }
      : null;
  };

  /**
   * @param {PointerEvent} event
   */
  #onPointerUp = (event) => {
    if (event.pointerType !== 'mouse') this.#lastTouch = event.timeStamp;
  };

  /**
   * Open submenus with a tap, and keep items without a destination from navigating
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const control = event.target instanceof Element ? event.target.closest('.menu-list__link') : null;
    const listItem = control?.closest('.menu-list__list-item');

    if (!control || !listItem) return;

    const item = findMenuItem(listItem);

    // Items without a submenu are plain links
    if (!item || !(listItem.slot === 'more' || findSubmenu(item))) return;

    const press =
      this.#press?.item === item && event.timeStamp - this.#press.timeStamp < PRESS_TIMEOUT ? this.#press : null;
    this.#press = null;

    const href = control.getAttribute('href');
    const hasDestination = control instanceof HTMLAnchorElement && Boolean(href) && !href?.startsWith('#');

    if (press && press.pointerType !== 'mouse') {
      // The first tap opens the submenu, a second tap on the open item follows its link
      if (press.wasOpen && hasDestination) return;

      event.preventDefault();

      if (press.wasOpen) {
        this.#deactivate(this.#state.activeItem, { force: true });
      } else {
        this.#activateItem(listItem);
      }
      return;
    }

    if (hasDestination) return;

    event.preventDefault();

    // Without a destination, the item toggles its submenu from the keyboard and opens it on a mouse click
    if (event.detail === 0 && item === this.#state.activeItem) {
      this.#deactivate(item, { force: true });
    } else {
      this.#activateItem(listItem);
    }
  };

  /**
   * Close the open submenu when a press lands outside the menu, such as a tap elsewhere on the page
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (!this.#state.activeItem || event.composedPath().includes(this)) return;

    this.#deactivate(this.#state.activeItem, { force: true });
  };

  /**
   * Close the open submenu on Escape, and return focus to its top-level item if focus was inside it
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    const item = this.#state.activeItem;

    if (event.key !== 'Escape' || !item) return;

    const control = this.#controlFor(item);
    const focusInside = this.#containsFocus(item);

    this.#debouncedActivate.cancel();
    this.#deactivate(item, { force: true });

    if (focusInside && control) {
      this.#restoringFocus = true;
      control.focus();
      this.#restoringFocus = false;
    }
  };

  /**
   * The always visible control that opens an item's submenu: the item itself, or the "More" button
   * for items in the overflow menu
   * @param {HTMLElement} item
   * @returns {HTMLElement | null}
   */
  #controlFor(item) {
    if (item.closest('.menu-list__list-item')?.slot !== 'overflow') return item;

    return this.querySelector('[slot="more"] .menu-list__link');
  }

  /**
   * Whether focus is on a menu item or inside its submenu (anywhere in the overflow menu for overflow items)
   * @param {HTMLElement} item
   * @returns {boolean}
   */
  #containsFocus(item) {
    const focused = document.activeElement;
    const listItem = item.closest('.menu-list__list-item');

    if (!focused || !listItem) return false;
    if (listItem.slot !== 'overflow') return listItem.contains(focused);

    return this.contains(focused) && Boolean(focused.closest('[slot="overflow"], [slot="more"]'));
  }

  /**
   * Follow the mouse while it moves over the menu or an open submenu
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    if (!this.#isMouse(event)) return;

    this.#trackPointer(event);

    if (!this.#pendingItem) return;

    if (this.#isHeadingIntoSubmenu()) {
      this.#restartPendingTimer();
    } else {
      this.#commitPending();
    }
  };

  /**
   * Record a mouse position, keeping the recent ones and the last one before them (where the mouse
   * rested before it started moving)
   * @param {PointerEvent} event
   */
  #trackPointer(event) {
    const trail = this.#trail;

    trail.push({ x: event.clientX, y: event.clientY, t: event.timeStamp });

    while (trail.length > 2 && event.timeStamp - trail[1].t > AIM_SAMPLE_WINDOW) {
      trail.shift();
    }
  }

  /**
   * Whether the mouse is moving towards the open submenu: its heading meets the submenu's top edge,
   * inside the "safe triangle" between the mouse and that edge
   * @returns {boolean}
   */
  #isHeadingIntoSubmenu() {
    const item = this.#state.activeItem;
    const submenu = this.dataset.overflowExpanded === 'true' ? this.overflowMenu : findSubmenu(item);
    const trail = this.#trail;
    const from = trail[0];
    const to = trail[trail.length - 1];

    if (!submenu || !from || !to || from === to) return false;

    const { top, left, right } = submenu.getBoundingClientRect();
    const dx = to.x - from.x;
    const dy = to.y - from.y;

    if (dy <= 0 || to.y >= top) return false;

    const x = to.x + (dx / dy) * (top - to.y);

    return x >= left && x <= right;
  }

  /**
   * Forget the item that was waiting for the mouse to rest on it
   */
  #cancelPending() {
    clearTimeout(this.#pendingTimer);
    this.#pendingItem = null;
  }

  /**
   * (Re)start waiting for the mouse to rest on the pending item
   */
  #restartPendingTimer() {
    clearTimeout(this.#pendingTimer);
    this.#pendingTimer = setTimeout(this.#commitPending, AIM_REST_DELAY);
  }

  /**
   * The mouse rested on the pending item, or turned away from the submenu: the item takes over
   */
  #commitPending = () => {
    const listItem = this.#pendingItem;

    this.#cancelPending();

    if (listItem?.matches(':hover')) {
      this.#activateItem(listItem);
    } else {
      this.#debouncedDeactivate();
    }
  };

  /**
   * Preload the submenu images, so they don't pop in when a submenu first opens. Only where the menu
   * is shown: on phones it is hidden, and its images aren't needed.
   */
  #preloadImages = () => {
    if (!this.getClientRects().length) {
      const observer = new ResizeObserver(() => {
        if (!this.getClientRects().length) return;

        observer.disconnect();
        this.#preloadImages();
      });

      observer.observe(this);
      this.#abortController.signal.addEventListener('abort', () => observer.disconnect());
      return;
    }

    for (const image of this.querySelectorAll('img[loading="lazy"]')) {
      // Images hidden by the current layout stay lazy, so they only load if the layout shows them
      if (image.getClientRects().length) image.removeAttribute('loading');
    }
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
